import React, { useEffect, useMemo, useState } from "react";

/** ========= Cognito (Hosted UI) — no frontend env =========
 * Replace COGNITO_DOMAIN and CLIENT_ID below.
 */
const COGNITO_DOMAIN = "https://karshuttle-admin.auth.ca-central-1.amazoncognito.com";
// ↑ keep this as whatever *exact* full domain you see in Cognito → App integration → Domain name

const CLIENT_ID      = "755910a9r3vfm1sdmbuk2shrpd";  // from aws-exports.js
const REDIRECT_URI   = "https://www.karshuttle.ca/admin";; // must be in Cognito callback URLs
const LOGOUT_URI     = "https://www.karshuttle.ca/";      // must be in Cognito sign-out URLs

const TOKEN_KEY  = "kar_admin_id_token";
const API_ORIGIN = "https://api.karrentals.ca";
const API_BASE   = `${API_ORIGIN}/shuttle/admin`;

/* ================= JWT helpers ================= */
const b64urlDecode = (str) => {
  try {
    const pad = "===".slice((str.length + 3) % 4);
    const s = str.replace(/-/g, "+").replace(/_/g, "/") + pad;
    return decodeURIComponent(
      atob(s)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
  } catch {
    return "";
  }
};

const decodeJwt = (jwt) => {
  if (!jwt || jwt.split(".").length !== 3) return null;
  const [, payload] = jwt.split(".");
  try {
    return JSON.parse(b64urlDecode(payload));
  } catch {
    return null;
  }
};

const isExpired = (jwt) => {
  const p = decodeJwt(jwt);
  if (!p?.exp) return true;
  // small skew
  return Date.now() / 1000 >= p.exp - 30;
};

/* ================= Auth helpers ================= */
const getToken = () => localStorage.getItem(TOKEN_KEY);
const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

const loginUrl = () => {
  const u = new URL(`${COGNITO_DOMAIN}/oauth2/authorize`);
  u.searchParams.set("client_id", CLIENT_ID);
  u.searchParams.set("response_type", "code");
  u.searchParams.set("scope", "openid email profile");
  u.searchParams.set("redirect_uri", REDIRECT_URI);
  return u.toString();
};

const logoutUrl = () => {
  const u = new URL(`${COGNITO_DOMAIN}/logout`);
  u.searchParams.set("client_id", CLIENT_ID);
  u.searchParams.set("logout_uri", LOGOUT_URI);
  return u.toString();
};

const login = () => {
  window.location.href = loginUrl();
};

const logout = () => {
  setToken(null);
  window.location.href = logoutUrl();
};

// Parse id_token from the URL hash after Cognito Hosted UI redirect
function useCognitoCallback() {
  useEffect(() => {
    const hash = window.location.hash?.slice(1) || "";
    if (!hash) return;
    const params = new URLSearchParams(hash);
    const idToken = params.get("id_token");
    if (idToken) {
      setToken(idToken);
    }
    // Clean hash
    if (idToken) {
      window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
    }
  }, []);
}

/* ============== small admin fetch wrapper (Bearer + 401 handling) ============== */
async function adminReq(path, { method = "GET", body } = {}) {
  const headers = { "Content-Type": "application/json" };
  const t = getToken();
  if (t) headers.Authorization = `Bearer ${t}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    cache: "no-store",
  });

  if (res.status === 401) {
    // token invalid/expired: clear and kick to login
    setToken(null);
    window.location.href = loginUrl();
    throw new Error("Unauthorized");
  }

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
    } catch {}
    throw new Error(msg);
  }
  return res.status === 204 ? null : res.json();
}

/** ================= Single-page Admin Portal ================= */
export default function AdminPortal() {
  useCognitoCallback();

  const token = getToken();
  const authed = !!token && !isExpired(token);

  if (!authed) {
    return (
      <CenterCard>
        <h1 className="text-xl font-semibold mb-3">KAR Shuttle • Admin</h1>
        <p className="text-sm text-gray-600 mb-6">Sign in to manage routes, blocks, and bookings.</p>
        <button
          onClick={login}
          className="w-full rounded-lg bg-gray-900 text-white py-2 font-semibold hover:opacity-90"
        >
          Continue with Cognito
        </button>
      </CenterCard>
    );
  }

  return <AdminScreen onLogout={logout} token={token} />;
}

/* ================= Sections (single page) ================= */
function AdminScreen({ onLogout, token }) {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // derive profile (for header greeting)
  const profile = useMemo(() => decodeJwt(token) || {}, [token]);

  async function loadDestinations() {
    setErr("");
    setLoading(true);
    try {
      const rows = await adminReq("/destinations");
      setDestinations(rows);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    loadDestinations();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="font-bold">KAR Shuttle • Admin</div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              {profile?.name || profile?.email || profile?.["cognito:username"] || "Admin"}
            </div>
            <button onClick={onLogout} className="text-sm text-gray-700 hover:text-black">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-8">
        {err && <Banner type="error">{err}</Banner>}
        {loading && <Banner>Loading…</Banner>}

        <DestinationsSection rows={destinations} refresh={loadDestinations} />

        <BlocksSection destinations={destinations} />

        <BookingsSection destinations={destinations} />
      </main>
    </div>
  );
}

/* -------- Destinations (list + inline edit + create) -------- */
function DestinationsSection({ rows, refresh }) {
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState({
    name: "",
    active: true,
    total_seats: 12,
    one_way_price_cents: 0,
    return_price_cents: 0,
    available_from: "",
    available_to: "",
    outbound_times: "",
    return_times: "",
  });
  const [busyCreate, setBusyCreate] = useState(false);
  const [err, setErr] = useState("");

  async function saveRow(r) {
    setErr("");
    setSavingId(r.id);
    // Avoid mutating original object: clone minimal fields
    const payload = toDestPayload({
      ...r,
      outbound_times: Array.isArray(r.outbound_times) ? r.outbound_times : splitTimes(r.outbound_times),
      return_times: Array.isArray(r.return_times) ? r.return_times : splitTimes(r.return_times),
    });

    try {
      await adminReq(`/destinations/${r.id}`, { method: "PATCH", body: payload });
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSavingId(null);
    }
  }

  async function createRow() {
    setErr("");
    setBusyCreate(true);
    try {
      const payload = toDestPayload({
        ...creating,
        outbound_times: splitTimes(creating.outbound_times),
        return_times: splitTimes(creating.return_times),
      });
      await adminReq(`/destinations`, { method: "POST", body: payload });
      setCreating({
        name: "",
        active: true,
        total_seats: 12,
        one_way_price_cents: 0,
        return_price_cents: 0,
        available_from: "",
        available_to: "",
        outbound_times: "",
        return_times: "",
      });
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusyCreate(false);
    }
  }

  return (
    <Section title="Destinations">
      {err && <Banner type="error">{err}</Banner>}

      <div className="overflow-x-auto bg-white border rounded-xl">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <Th>ID</Th>
              <Th>Name</Th>
              <Th>Active</Th>
              <Th>Seats</Th>
              <Th>One-way (¢)</Th>
              <Th>Return (¢)</Th>
              <Th>Window</Th>
              <Th>Outbound</Th>
              <Th>Return</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t align-top">
                <Td>{r.id}</Td>
                <Td>
                  <Input value={r.name} onChange={(v) => (r.name = v)} />
                </Td>
                <Td>
                  <Checkbox checked={!!r.active} onChange={(v) => (r.active = v)} />
                </Td>
                <Td>
                  <NumberInput value={r.total_seats} onChange={(v) => (r.total_seats = v)} />
                </Td>
                <Td>
                  <NumberInput value={r.one_way_price_cents} onChange={(v) => (r.one_way_price_cents = v)} />
                </Td>
                <Td>
                  <NumberInput value={r.return_price_cents} onChange={(v) => (r.return_price_cents = v)} />
                </Td>
                <Td>
                  <div className="flex gap-2">
                    <Input
                      placeholder="from (YYYY-MM-DD)"
                      value={r.available_from || ""}
                      onChange={(v) => (r.available_from = v)}
                    />
                    <Input
                      placeholder="to (YYYY-MM-DD)"
                      value={r.available_to || ""}
                      onChange={(v) => (r.available_to = v)}
                    />
                  </div>
                </Td>
                <Td>
                  <Input
                    value={(r.outbound_times || []).join(", ")}
                    onChange={(v) => (r.outbound_times = splitTimes(v))}
                  />
                </Td>
                <Td>
                  <Input
                    value={(r.return_times || []).join(", ")}
                    onChange={(v) => (r.return_times = splitTimes(v))}
                  />
                </Td>
                <Td>
                  <button
                    onClick={() => saveRow(r)}
                    disabled={savingId === r.id}
                    className="rounded-lg bg-blue-600 text-white px-3 py-1"
                  >
                    {savingId === r.id ? "Saving…" : "Save"}
                  </button>
                </Td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <Td colSpan={10}>
                  <span className="text-gray-500">No destinations.</span>
                </Td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* create */}
      <div className="bg-white border rounded-xl p-4 mt-4 space-y-3">
        <div className="font-semibold">Create destination</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Input label="Name" value={creating.name} onChange={(v) => setCreating((f) => ({ ...f, name: v }))} />
          <div className="flex items-end gap-2">
            <label className="text-sm text-gray-600">Active</label>
            <Checkbox
              checked={creating.active}
              onChange={(v) => setCreating((f) => ({ ...f, active: v }))}
            />
          </div>
          <NumberInput
            label="Seats"
            value={creating.total_seats}
            onChange={(v) => setCreating((f) => ({ ...f, total_seats: v }))}
          />
          <NumberInput
            label="One-way (¢)"
            value={creating.one_way_price_cents}
            onChange={(v) => setCreating((f) => ({ ...f, one_way_price_cents: v }))}
          />
          <NumberInput
            label="Return (¢)"
            value={creating.return_price_cents}
            onChange={(v) => setCreating((f) => ({ ...f, return_price_cents: v }))}
          />
          <Input
            label="Available from (YYYY-MM-DD)"
            value={creating.available_from}
            onChange={(v) => setCreating((f) => ({ ...f, available_from: v }))}
          />
          <Input
            label="Available to (YYYY-MM-DD)"
            value={creating.available_to}
            onChange={(v) => setCreating((f) => ({ ...f, available_to: v }))}
          />
          <Input
            label="Outbound times (comma HH:MM)"
            value={creating.outbound_times}
            onChange={(v) => setCreating((f) => ({ ...f, outbound_times: v }))}
          />
          <Input
            label="Return times (comma HH:MM)"
            value={creating.return_times}
            onChange={(v) => setCreating((f) => ({ ...f, return_times: v }))}
          />
        </div>
        <button
          onClick={createRow}
          disabled={busyCreate}
          className="rounded-lg bg-blue-600 text-white px-4 py-2"
        >
          {busyCreate ? "Creating…" : "Create"}
        </button>
      </div>
    </Section>
  );
}

function toDestPayload(r) {
  return {
    name: r.name,
    active: !!r.active,
    total_seats: Number(r.total_seats || 0),
    one_way_price_cents: Number(r.one_way_price_cents || 0),
    return_price_cents: Number(r.return_price_cents || 0),
    available_from: r.available_from || null,
    available_to: r.available_to || null,
    outbound_times: Array.isArray(r.outbound_times) ? r.outbound_times : splitTimes(r.outbound_times),
    return_times: Array.isArray(r.return_times) ? r.return_times : splitTimes(r.return_times),
  };
}

const splitTimes = (v) => String(v || "").split(",").map((s) => s.trim()).filter(Boolean);

/* -------- Blocks (date + time) -------- */
function BlocksSection({ destinations }) {
  const [destId, setDestId] = useState("");
  const [dateBlocks, setDateBlocks] = useState([]);
  const [timeBlocks, setTimeBlocks] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!destId) return;
    (async () => {
      setErr("");
      try {
        const [db, tb] = await Promise.all([
          adminReq(`/date-blocks/${destId}`),
          adminReq(`/time-blocks/${destId}`),
        ]);
        setDateBlocks(db);
        setTimeBlocks(tb);
      } catch (e) {
        setErr(e.message);
      }
    })();
  }, [destId]);

  const [newDate, setNewDate] = useState("");
  const [savingDate, setSavingDate] = useState(false);

  const [newTB, setNewTB] = useState({ service_date: "", leg: "outbound", time_slot: "" });
  const [savingTB, setSavingTB] = useState(false);

  async function addDateBlock() {
    if (!destId || !newDate) return;
    setSavingDate(true);
    try {
      await adminReq(`/date-blocks`, { method: "POST", body: { destination_id: destId, block_date: newDate } });
      setNewDate("");
      setDateBlocks(await adminReq(`/date-blocks/${destId}`));
    } catch (e) {
      setErr(e.message);
    } finally {
      setSavingDate(false);
    }
  }

  async function removeDateBlock(id) {
    await adminReq(`/date-blocks/${id}`, { method: "DELETE" });
    setDateBlocks(await adminReq(`/date-blocks/${destId}`));
  }

  async function addTimeBlock() {
    if (!destId || !newTB.service_date || !newTB.time_slot) return;
    setSavingTB(true);
    try {
      await adminReq(`/time-blocks`, {
        method: "POST",
        body: { destination_id: destId, ...newTB },
      });
      setNewTB({ service_date: "", leg: "outbound", time_slot: "" });
      setTimeBlocks(await adminReq(`/time-blocks/${destId}`));
    } catch (e) {
      setErr(e.message);
    } finally {
      setSavingTB(false);
    }
  }

  async function removeTimeBlock(id) {
    await adminReq(`/time-blocks/${id}`, { method: "DELETE" });
    setTimeBlocks(await adminReq(`/time-blocks/${destId}`));
  }

  return (
    <Section title="Blocks">
      {err && <Banner type="error">{err}</Banner>}

      <div className="mb-3">
        <label className="text-sm text-gray-600 mr-2">Destination</label>
        <select
          className="border rounded-lg px-3 py-2"
          value={destId}
          onChange={(e) => setDestId(e.target.value)}
        >
          <option value="">Select…</option>
          {destinations.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      </div>

      {destId && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border rounded-xl p-4">
            <div className="font-semibold mb-2">Date blocks</div>
            <div className="flex gap-2 mb-3">
              <Input placeholder="YYYY-MM-DD" value={newDate} onChange={setNewDate} />
              <button
                onClick={addDateBlock}
                disabled={savingDate}
                className="rounded-lg bg-blue-600 text-white px-3 py-2"
              >
                {savingDate ? "Adding…" : "Add"}
              </button>
            </div>
            <List
              items={dateBlocks}
              render={(r) => (
                <Row>
                  <span>{r.block_date}</span>
                  <BtnLink onClick={() => removeDateBlock(r.id)} danger>
                    Remove
                  </BtnLink>
                </Row>
              )}
            />
          </div>

          <div className="bg-white border rounded-xl p-4">
            <div className="font-semibold mb-2">Time blocks</div>
            <div className="grid sm:grid-cols-4 gap-2 mb-3">
              <Input
                placeholder="YYYY-MM-DD"
                value={newTB.service_date}
                onChange={(v) => setNewTB((s) => ({ ...s, service_date: v }))}
              />
              <select
                className="border rounded-lg px-3 py-2"
                value={newTB.leg}
                onChange={(e) => setNewTB((s) => ({ ...s, leg: e.target.value }))}
              >
                <option value="outbound">outbound</option>
                <option value="return">return</option>
              </select>
              <Input
                placeholder="HH:MM"
                value={newTB.time_slot}
                onChange={(v) => setNewTB((s) => ({ ...s, time_slot: v }))}
              />
              <button
                onClick={addTimeBlock}
                disabled={savingTB}
                className="rounded-lg bg-blue-600 text-white px-3 py-2"
              >
                {savingTB ? "Adding…" : "Add"}
              </button>
            </div>
            <List
              items={timeBlocks}
              render={(r) => (
                <Row>
                  <span>
                    {r.service_date} • {r.leg} • {r.time_slot}
                  </span>
                  <BtnLink onClick={() => removeTimeBlock(r.id)} danger>
                    Remove
                  </BtnLink>
                </Row>
              )}
            />
          </div>
        </div>
      )}
    </Section>
  );
}

/* -------- Bookings (filter + cancel) -------- */
function BookingsSection({ destinations }) {
  const [filters, setFilters] = useState({ date: "", destination_id: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.date) p.set("date", filters.date);
    if (filters.destination_id) p.set("destination_id", filters.destination_id);
    return p.toString() ? `?${p.toString()}` : "";
  }, [filters]);

  async function load() {
    setErr("");
    setLoading(true);
    try {
      setRows(await adminReq(`/bookings${qs}`));
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs]);

  async function cancel(id) {
    await adminReq(`/bookings/${id}/cancel`, { method: "POST" });
    load();
  }

  return (
    <Section title="Bookings">
      {err && <Banner type="error">{err}</Banner>}

      <div className="flex flex-wrap gap-2 mb-3">
        <Input
          placeholder="YYYY-MM-DD"
          value={filters.date}
          onChange={(v) => setFilters((f) => ({ ...f, date: v }))}
        />
        <select
          className="border rounded-lg px-3 py-2"
          value={filters.destination_id}
          onChange={(e) => setFilters((f) => ({ ...f, destination_id: e.target.value }))}
        >
          <option value="">All destinations</option>
          {destinations.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <button onClick={load} className="rounded-lg border px-3 py-2">
          Refresh
        </button>
      </div>

      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className="overflow-x-auto bg-white border rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th>#</Th>
                <Th>Route</Th>
                <Th>Date/Time</Th>
                <Th>Leg</Th>
                <Th>Seats</Th>
                <Th>Contact</Th>
                <Th>Status</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <Td>{r.id}</Td>
                  <Td>{r.destination_name}</Td>
                  <Td>
                    {r.service_date} {r.time_slot}
                  </Td>
                  <Td>{r.leg}</Td>
                  <Td>{r.seats_reserved}</Td>
                  <Td>
                    <div>{r.contact_name}</div>
                    <div className="text-xs text-gray-600">
                      {r.contact_email}
                      {r.contact_phone ? `, ${r.contact_phone}` : ""}
                    </div>
                  </Td>
                  <Td>{r.status}</Td>
                  <Td>
                    {r.status !== "cancelled" && (
                      <BtnLink danger onClick={() => cancel(r.id)}>
                        Cancel
                      </BtnLink>
                    )}
                  </Td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <Td colSpan={8}>
                    <span className="text-gray-500">No bookings.</span>
                  </Td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

/* ================= micro UI helpers ================= */
function Section({ title, children }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Banner({ type = "info", children }) {
  const styles =
    type === "error"
      ? "text-red-700 bg-red-50 border-red-200"
      : "text-blue-700 bg-blue-50 border-blue-200";
  return <div className={`border rounded-xl p-3 mb-3 ${styles}`}>{children}</div>;
}

function CenterCard({ children }) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="max-w-sm w/full border rounded-2xl p-6 shadow-sm bg-white">{children}</div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left p-3">{children}</th>;
}
function Td({ children, colSpan }) {
  return (
    <td className="p-3" colSpan={colSpan}>
      {children}
    </td>
  );
}
function Row({ children }) {
  return <div className="flex items-center justify-between border rounded-lg px-3 py-2">{children}</div>;
}
function List({ items, render }) {
  return items.length ? (
    <div className="space-y-2">{items.map(render)}</div>
  ) : (
    <div className="text-sm text-gray-500">None.</div>
  );
}
function BtnLink({ children, onClick, danger }) {
  return (
    <button onClick={onClick} className={`hover:underline ${danger ? "text-red-700" : "text-blue-700"}`}>
      {children}
    </button>
  );
}
function Input({ label, value, onChange, placeholder }) {
  return (
    <label className="block">
      {label && <div className="text-sm text-gray-600 mb-1">{label}</div>}
      <input
        className="w-full border rounded-lg px-3 py-2"
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
function NumberInput({ label, value, onChange }) {
  return (
    <label className="block">
      {label && <div className="text-sm text-gray-600 mb-1">{label}</div>}
      <input
        type="number"
        className="w-full border rounded-lg px-3 py-2"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}
function Checkbox({ checked, onChange }) {
  return <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />;
}
